export class VideoHover {
	constructor(container, options = {}) {
		this.container = container;
		this.options = {
			videoSrc: container.dataset.videoSrc,
			previewSrc: container.dataset.previewSrc,
			linkReadmore: container.dataset.linkReadmore,
			hoverDelay: options.hoverDelay || 100,
			fadeDuration: options.fadeDuration || 300,
			...options,
		};

		this.video = null;
		this.preview = null;
		this.isPlaying = false;
		this.hoverTimeout = null;
		this.reverseInterval = null;

		this.init();
	}

	init() {
		if (!this.options.videoSrc || !this.options.previewSrc) {
			console.warn(
				"VideoHover: Missing videoSrc or previewSrc",
				this.container,
			);
			return;
		}

		this.createVideoElement();
		this.setupEventListeners();
		this.applyStyles();
	}

	createVideoElement() {
		// Создаем видео элемент
		this.video = document.createElement("video");
		this.video.className = "video-hover-player";
		this.video.src = this.options.videoSrc;
		this.video.muted = true;
		this.video.loop = true;
		this.video.playsInline = true;
		this.video.preload = "metadata";

		// Создаем превью
		this.preview = document.createElement("img");
		this.preview.className = "video-hover-preview";
		this.preview.src = this.options.previewSrc;
		this.preview.alt = "Video preview";

		// Очищаем контейнер и добавляем элементы
		this.container.innerHTML = "";
		this.container.appendChild(this.preview);
		this.container.appendChild(this.video);
	}

	applyStyles() {
		// Стили контейнера
		this.container.style.position = "relative";
		this.container.style.display = "block";
		this.container.style.overflow = "hidden";
		this.container.style.cursor = "pointer";

		// Стили превью
		this.preview.style.width = "100%";
		this.preview.style.height = "100%";
		this.preview.style.objectFit = "cover";
		this.preview.style.transition = `opacity ${this.options.fadeDuration}ms ease`;
		this.preview.style.display = "block";

		// Стили видео
		this.video.style.position = "absolute";
		this.video.style.top = "0";
		this.video.style.left = "0";
		this.video.style.width = "100%";
		this.video.style.height = "100%";
		this.video.style.objectFit = "cover";
		this.video.style.opacity = "0";
		this.video.style.transition = `opacity ${this.options.fadeDuration}ms ease`;
		this.video.style.pointerEvents = "none";
		this.video.loop = false;
	}

	lazyPreloadVideo() {
		if (this.video.preload !== "none") {
			// Видео уже предзагружено или загружается
			return;
		}

		// Устанавливаем preload и загружаем видео
		this.video.preload = "metadata";

		this.video.addEventListener(
			"loadeddata",
			() => {
				console.log("VideoHover: Video lazy loaded", this.options.videoSrc);
			},
			{ once: true },
		);

		this.video.addEventListener(
			"error",
			(error) => {
				console.warn(
					"VideoHover: Lazy load failed",
					this.options.videoSrc,
					error,
				);
			},
			{ once: true },
		);

		// Запускаем загрузку
		this.video.load();
	}

	setupEventListeners() {
		this.container.addEventListener("click", this.handleClick.bind(this));

		this.container.addEventListener(
			"mouseenter",
			this.handleMouseEnter.bind(this),
		);
		this.container.addEventListener(
			"mouseleave",
			this.handleMouseLeave.bind(this),
		);
		this.container.addEventListener("touchstart", this.handleTouch.bind(this), {
			passive: false,
		});

		// Предзагрузка видео при первом наведении
		this.container.addEventListener(
			"mouseenter",
			this.lazyPreloadVideo.bind(this),
			{ once: true },
		);
	}

	handleClick() {
		window.location.href = this.options.linkReadmore;
	}

	handleMouseEnter() {
		this.hoverTimeout = setTimeout(() => {
			this.playVideo();
		}, this.options.hoverDelay);
	}

	handleMouseLeave() {
		if (this.hoverTimeout) {
			clearTimeout(this.hoverTimeout);
			this.hoverTimeout = null;
		}
		this.pauseVideo();
		this.startReverse();
	}

	handleTouch(e) {
		e.preventDefault();
		if (this.isPlaying) {
			this.pauseVideo();
		} else {
			this.playVideo();
		}
	}

	async playVideo() {
		if (this.isPlaying) return;

		try {
			this.preview.style.opacity = "0";
			this.video.style.opacity = "1";
			this.stopReverse();
			await this.video.play();
			this.isPlaying = true;
		} catch (error) {
			console.error("VideoHover: Play failed", error);
			// Возвращаем превью если видео не запустилось
			this.preview.style.opacity = "1";
			this.video.style.opacity = "0";
		}
	}

	pauseVideo() {
		if (!this.isPlaying) return;

		this.video.pause();
		this.isPlaying = false;
	}

	// Публичные методы для управления
	play() {
		this.playVideo();
	}

	pause() {}

	startReverse() {
		this.stopReverse();

		// Видео должно быть видно при реверсе
		this.preview.style.opacity = "0";
		this.video.style.opacity = "1";

		this.video.pause();

		this.reverseInterval = setInterval(() => {
			this.video.currentTime -= 0.24;

			if (this.video.currentTime <= 0) {
				this.video.currentTime = 0;
				this.stopReverse();

				// Когда дошли до начала — показать превью
				this.preview.style.opacity = "1";
				this.video.style.opacity = "0";
			}
		}, 40);
	}

	stopReverse() {
		if (this.reverseInterval) {
			clearInterval(this.reverseInterval);
			this.reverseInterval = null;
		}
	}

	destroy() {
		if (this.hoverTimeout) {
			clearTimeout(this.hoverTimeout);
		}

		this.container.removeEventListener("mouseenter", this.handleMouseEnter);
		this.container.removeEventListener("mouseleave", this.handleMouseLeave);
		this.container.removeEventListener("touchstart", this.handleTouch);

		this.pauseVideo();
		this.video = null;
		this.preview = null;
	}
}

// Вспомогательная функция для инициализации всех элементов
// export function initVideoHover(selector = ".video-item", options = {}) {
// 	const instances = [];

// 	document.querySelectorAll(selector).forEach((container) => {
// 		try {
// 			const instance = new VideoHover(container, options);
// 			instances.push(instance);
// 		} catch (error) {
// 			console.error("VideoHover: Init failed for", container, error);
// 		}
// 	});

// 	return instances;
// }
